import { css } from '@emotion/css';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import { sendEvent } from '../../../lib/google-analytics';
import { usePrefersReducedMotion } from '../../shared/usePrefersReducedMotion';

interface NotificationCardProps {
  onDismiss: () => void;
}

const yStops = [0, 64];
const threshold = 20;

const decelerationRate = 0.998;

const projection = (initialVelocity: number) =>
  (initialVelocity * decelerationRate) / (1 - decelerationRate);

const findNearest = (n: number, arr: number[]): number | undefined => {
  const sortedArr = [...arr].sort((a, b) => a - b);
  if (n <= sortedArr[0]) {
    return sortedArr[0];
  }
  if (n >= sortedArr[arr.length - 1]) {
    return sortedArr[arr.length - 1];
  }
  for (let i = 1; i < sortedArr.length; i++) {
    const prev = sortedArr[i - 1];
    const current = sortedArr[i];
    if (current === n) {
      return current;
    }
    if (current > n && prev < n) {
      return current - n < n - prev ? current : prev;
    }
  }
  return undefined;
};

export const NotificationCard: React.FC<NotificationCardProps> = ({ onDismiss, children }) => {
  const [{ y }, setPosition] = useSpring(() => ({ y: yStops[1] }));
  const timerToClear = useRef<NodeJS.Timeout | undefined>(undefined);
  const prefersReducedMotion = usePrefersReducedMotion();
  const history = useHistory();
  const handleCardClick = () => {
    sendEvent('home_notification_center', 'click', 'notification_card');
    const pageParam = history.location.search;
    history.push(`/players/home/notifications${pageParam}`);
    onDismiss();
  };
  useEffect(() => {
    setPosition({ y: yStops[0], immediate: prefersReducedMotion });
    timerToClear.current = setTimeout(() => {
      setPosition({ y: yStops[1], immediate: prefersReducedMotion }).then(({ finished }) => {
        if (finished) {
          onDismiss();
        }
      });
    }, 3000);
    return () => {
      if (timerToClear.current) {
        clearTimeout(timerToClear.current);
      }
    };
  }, [setPosition, onDismiss, prefersReducedMotion]);

  const bind = useDrag(({ movement, vxvy, memo, last }) => {
    const [, my] = movement;
    const [, vy] = vxvy;

    const sy = y.get();

    if (!memo) {
      if (Math.abs(my) <= threshold) {
        return;
      }
      if (timerToClear.current) {
        clearInterval(timerToClear.current);
      }
      memo = my - sy;
    }

    if (last) {
      const projected = y.get() - projection(vy);
      const point = findNearest(projected, yStops);
      const dismissed = point === yStops[1];
      setPosition({
        y: dismissed ? yStops[1] : yStops[0],
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onRest: dismissed ? onDismiss : () => {}
      });
      return;
    }

    const nextY = memo - my;
    setPosition({ y: nextY, immediate: true });

    return memo;
  });

  return (
    <animated.div
      className={css`
        position: absolute;
        top: 20px;
        left: 16px;
        right: 16px;
        z-index: 1;
      `}
      style={{
        opacity: y.to(yStops, [1, 0]),
        transform: y.to(sy => `translate3d(0, -${sy}px, 0)`)
      }}
      onTouchStart={bind().onTouchStart}
      onClick={handleCardClick}
      aria-live="assertive"
    >
      {children}
    </animated.div>
  );
};
